<template>
  <WidgetCadre
    titre="CA du mois"
    v-bind="apparence"
    :lien="donnees?.lien || '/compareCA'"
    libelle-lien="Détail"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <p class="ca__total">
      <span class="chiffre chiffre--grand">{{ euros(donnees?.ca_total) }}</span>

      <!-- Pas de flèche quand le mois précédent est à zéro : le serveur rend
           `null`, et « +100 % » depuis rien serait un chiffre faux. -->
      <span
        v-if="tendance !== null"
        class="ca__tendance"
        :class="tendance >= 0 ? 'ca__tendance--hausse' : 'ca__tendance--baisse'"
      >{{ tendance >= 0 ? '+' : '−' }}{{ Math.abs(tendance).toLocaleString('fr-FR') }} %</span>
    </p>

    <p class="etiquette ca__precedent">
      {{ euros(donnees?.precedent) }} le mois dernier
    </p>

    <dl class="ca__repartition">
      <div>
        <dt class="etiquette">
          Abonnement
        </dt>
        <dd class="chiffre chiffre--moyen">
          {{ euros(donnees?.ca_abo) }}
        </dd>
      </div>
      <div>
        <dt class="etiquette">
          Consommation
        </dt>
        <dd class="chiffre chiffre--moyen">
          {{ euros(donnees?.ca_conso) }}
        </dd>
      </div>
      <div>
        <dt class="etiquette">
          Clients
        </dt>
        <dd class="chiffre chiffre--moyen">
          {{ donnees?.nb_clients ?? '—' }}
        </dd>
      </div>
    </dl>

    <!-- Le périmètre vient du serveur et s'affiche tel quel : ce chiffre est
         incomplet d'une manière connue de ceux qui le lisent, et le taire
         reviendrait à le présenter comme le CA total. -->
    <p
      v-if="donnees?.perimetre"
      class="ca__perimetre"
    >
      {{ donnees.perimetre }}
    </p>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('ca-du-mois')

/** `null` est une valeur, pas une absence : elle dit « pas de comparaison possible ». */
const tendance = computed(() =>
    typeof props.donnees?.tendance === 'number' ? props.donnees.tendance : null,
)

function euros(montant) {
    if (montant === null || montant === undefined) return '—'

    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(montant)
}
</script>

<style lang="scss" scoped>
.ca {
  &__total {
    display: flex;
    align-items: baseline;
    gap: 9px;
    margin: 0;
    flex-wrap: wrap;
  }

  &__tendance {
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    &--hausse { color: var(--accueil-sain); }
    &--baisse { color: #c0392b; }
  }

  &__precedent {
    margin: 3px 0 0;
  }

  &__repartition {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin: 12px 0 0;
    padding-top: 10px;
    border-top: 1px solid var(--el-border-color-lighter);

    dt { margin: 0; }
    dd { margin: 2px 0 0; }
  }

  &__perimetre {
    margin: 9px 0 0;
    font-size: 11px;
    line-height: 1.35;
    color: var(--el-text-color-placeholder);
  }
}
</style>
